'use client';

import { useEffect, useState, useRef } from 'react';
import { BREAKPOINTS } from '@libs/common-next';
import type { MutableRefObject } from 'react';

interface WindowDimensions {
    width: number;
    height: number;
}

const BREADCRUMB_ITEM_ELLIPSIS = 'breadcrumbs__item--ellipsis';
const BREADCRUMB_ITEM_TRUNCATE = 'breadcrumbs__item--truncate';

// Base 16 to convert px to rem
const toRem = (px: number) => (1 / 16) * px;

const getWindowDimensions = (): WindowDimensions | undefined => {
    if (window) {
        const { innerWidth: width, innerHeight: height } = window;
        return {
            width,
            height,
        };
    }
    return undefined;
};

const resetAppliedStyles = (breadcrumbs: NodeListOf<HTMLLIElement>) => {
    breadcrumbs.forEach(item => {
        item.classList.remove(BREADCRUMB_ITEM_ELLIPSIS);
        item.classList.remove(BREADCRUMB_ITEM_TRUNCATE);
        item.style.removeProperty('max-width');
    });
};

export const adjustBreadcrumbsToScreen = (
    containerRef: MutableRefObject<HTMLDivElement | undefined>,
    isLastItemVisible: boolean
) => {
    const container = containerRef.current;
    const breadcrumbs = container?.querySelectorAll('li');

    if (!container || !breadcrumbs) {
        return;
    }

    // Reset all added classes
    resetAppliedStyles(breadcrumbs);

    const lastVisibleItemIndex = isLastItemVisible
        ? breadcrumbs.length - 1
        : breadcrumbs.length - 2;

    const firstVisibleItem = breadcrumbs[0];
    const lastVisibleItem = breadcrumbs[lastVisibleItemIndex];

    // Start by replacing inner items with ellipsis to fit breadcrumbs in a single line
    for (let i = 1; i < lastVisibleItemIndex; i++) {
        if (firstVisibleItem.offsetTop === lastVisibleItem.offsetTop) {
            return;
        }
        breadcrumbs[i].classList.add(BREADCRUMB_ITEM_ELLIPSIS);
    }

    // Continue by truncating last visible item
    lastVisibleItem.classList.add(BREADCRUMB_ITEM_TRUNCATE);
    let itemMaxWidth = toRem(BREAKPOINTS.sm);

    do {
        // Reduce maxWidth by 0.5rem until breadcrumbs fit in a single line
        itemMaxWidth -= 0.5;
        lastVisibleItem.style.maxWidth = `${itemMaxWidth}rem`;
    } while (
        firstVisibleItem.offsetTop !== lastVisibleItem.offsetTop &&
        itemMaxWidth > 0
    );
};

function BreadcrumbsAdjuster({
    containerSelector,
    isLastItemVisible,
}: {
    containerSelector: string;
    isLastItemVisible: boolean;
}) {
    const [width, setWidth] = useState<WindowDimensions | undefined>();
    const containerRef = useRef<HTMLDivElement | undefined>();

    const handleWindowResize = () => {
        setWidth(getWindowDimensions());
    };

    useEffect(() => {
        const breadcrumbsContainer = document.querySelector(containerSelector);
        if (breadcrumbsContainer) {
            containerRef.current = breadcrumbsContainer as HTMLDivElement;
        }

        window.addEventListener('resize', handleWindowResize);
        return () => window.removeEventListener('resize', handleWindowResize);
    }, [containerSelector]);

    useEffect(() => {
        // Accounts for time CSS takes to adjust
        setTimeout(() => {
            adjustBreadcrumbsToScreen(containerRef, isLastItemVisible);
        }, 1000);
    }, [isLastItemVisible, width]);

    // eslint-disable-next-line react/jsx-no-useless-fragment -- client-side util
    return <></>;
}

export default BreadcrumbsAdjuster;
